import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { authenticatedUser, logout } from "../../../../features/auth/redux/slice";
import type { AppDispatch } from "../../../redux/types";
import useTranslation from "../../../hooks/useTranslation";
import { LOCALES } from "../../../constants/locales";

export default function UserMenu() {
    const { t, locale, setLocale } = useTranslation();
    const { username, role } = useSelector(authenticatedUser);
    const dispatch = useDispatch<AppDispatch>();
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    const handleLogout = useCallback(() => dispatch(logout()), [dispatch]);
    const otherLocale = LOCALES.filter((l: string) => l !== locale)[0];

    useEffect(() => {
        if (!open) return undefined;
        const onClick = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    return (
        <div ref={rootRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                title={username ?? t("user_menu.dropdown.title")}
                aria-label={username ?? t("user_menu.dropdown.title")}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg! bg-accent text-sm font-semibold text-white transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-accent/40"
            >
                {username?.[0]?.toUpperCase() ?? "?"}
            </button>
            {open && (
                <div
                    role="menu"
                    className="absolute right-0 mt-2 w-64 rounded-xl border border-border bg-surface py-2 shadow-lg"
                >
                    <div className="flex items-center gap-3 px-4 py-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent text-sm font-semibold text-white">
                            {username?.[0]?.toUpperCase() ?? "?"}
                        </span>
                        <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-ink">{username}</div>
                            {role && (
                                <div className="truncate text-xs text-ink-muted">
                                    {t(`attributes.user.role.${role}`)}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="my-1 border-t border-border" />
                    <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                            setLocale(otherLocale);
                            setOpen(false);
                        }}
                        className="block w-full cursor-pointer px-4 py-2 text-left text-sm text-ink hover:bg-surface-3"
                    >
                        {t(`locales.native.${otherLocale}`)}
                    </button>
                    <div className="my-1 border-t border-border" />
                    <button
                        type="button"
                        role="menuitem"
                        onClick={handleLogout}
                        title={t("actions.logout")}
                        className="block w-full cursor-pointer px-4 py-2 text-left text-sm text-ink hover:bg-surface-3"
                    >
                        {t("actions.logout")}
                    </button>
                </div>
            )}
        </div>
    );
}
